import { useEffect, useState, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

/**
 * Hook personalizado para gerenciamento de dados do dashboard
 * 
 * @param {string} dateFilter - Filtro de período para busca de dados
 * @param {string} baseURL - URL base para requisições da API
 * @returns {Object} Objeto com dados do dashboard, estado de carregamento e função de ordenação
 * 
 * Funcionalidades principais:
 * - Busca de dados de resumo e gráfico
 * - Autenticação via token JWT
 * - Filtragem de dados por período
 * - Ordenação de dados do gráfico
 */
export default function useDashboardData(dateFilter, baseURL, setForceTokenExpired) {
  // Estado para controle de carregamento de dados
  const [loading, setLoading] = useState(false);


  // Estado para armazenar resumo de métricas
  const [resumo, setResumo] = useState({
    gestores: 0,    // Total de mensagens de gestores
    corretores: 0,  // Total de mensagens de corretores
    novoLead: 0     // Total de novos leads
  });

  // Estado para armazenar dados brutos do gráfico
  const [rawChartData, setRawChartData] = useState([]);

  // Estado para controlar opção de ordenação dos dados
  const [sortOption, setSortOption] = useState('alfabetica');

  /**
   * Converte filtro de data para número de dias
   * Usado para definir o intervalo de busca de dados
   * 
   * @param {string} filter - Filtro de período selecionado
   * @returns {number} Número de dias correspondente ao filtro
   */
  const mapFilterToDays = (filter) => {
    switch (filter) {
      case 'Todos': return 5000;           // Praticamente todos os registros
      case 'Mês atual': return new Date().getDate(); // Dias do mês atual
      case 'Últimos 7 dias': return 7;     // Últimos 7 dias
      case 'Últimos 30 dias': return 30;   // Últimos 30 dias
      case 'Últimos 3 meses': return 90;   // Últimos 3 meses
      case 'Últimos 6 meses': return 180;  // Últimos 6 meses
      case 'Últimos 12 meses': return 365; // Últimos 12 meses
      default : return 0;
    }
  };

  /**
   * Hook de efeito para buscar dados do dashboard
   * Executa requisições à API quando filtro ou URL base mudam
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    /**
     * Função assíncrona para buscar dados do dashboard
     * Realiza duas requisições paralelas:
     * 1. Busca totais de mensagens
     * 2. Busca totais de mensagens por corretor
     */
    const fetchDashboard = async () => {
      console.log('🚀 [DASHBOARD] Iniciando busca de dados do dashboard');
      console.log('📅 [DASHBOARD] Filtro de data atual:', dateFilter);
      console.log('🌐 [DASHBOARD] Base URL:', baseURL);

      // Recupera token de autenticação do localStorage
      const token = localStorage.getItem('jwtToken');
      console.log('🔑 [AUTH] Token encontrado:', token ? 'SIM' : 'NÃO');

      if (!token) {
        console.error('❌ [AUTH] Token não encontrado no localStorage');
        return;
      }

      // Indica início do carregamento
      setLoading(true);
      console.log('⏳ [LOADING] Estado de carregamento ativado');

      try {
        // Decodifica token para obter ID do cliente
        const decoded = jwtDecode(token);
        const idCliente = decoded.id_cliente;
        console.log('🔓 [AUTH] Token decodificado com sucesso');
        console.log('👤 [AUTH] ID do cliente:', idCliente);

        // Converte filtro de data para número de dias
        const dias = mapFilterToDays(dateFilter);
        console.log('📊 [FILTER] Dias calculados:', dias);

        // Log das URLs que serão chamadas
        const totaisUrl = `${baseURL}/produto/AV/totais-mensagens`;
        const corretorUrl = `${baseURL}/produto/AV/corretor-totais-mensagens`;

        console.log('🔗 [API] URL totais:', totaisUrl);
        console.log('🔗 [API] URL corretor:', corretorUrl);
        console.log('📋 [API] Parâmetros:', { idCliente, dias });

        // Realiza requisições paralelas para otimizar performance
        console.log('📡 [API] Iniciando requisições paralelas...');

        const [totRes, chartRes] = await Promise.all([
          // Requisição de totais de mensagens
          axios.get(`${baseURL}/produto/AV/totais-mensagens`, {
            params: { idCliente, dias },
            headers: { Authorization: `Bearer ${token}` }
          }),
          // Requisição de totais de mensagens por corretor
          axios.get(`${baseURL}/produto/AV/corretor-totais-mensagens`, {
            params: { idCliente, dias },
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        console.log('✅ [API] Requisições completadas com sucesso');

        // Log detalhado da resposta de totais
        console.log('📊 [TOTAIS] Resposta completa:', totRes.data);
        console.log('📊 [TOTAIS] Status da resposta:', totRes.status);

        // Log específico dos valores de resumo
        console.log('📈 [RESUMO] Total mensagens gestores:', totRes.data.totalMsgResumoGestores);
        console.log('📈 [RESUMO] Total mensagens corretores:', totRes.data.totalMsgResumoCorretores);
        console.log('📈 [RESUMO] Total novo lead corretores:', totRes.data.totalMsgNovoLeadCorretores);

        // Verificação se os valores existem
        if (totRes.data.totalMsgResumoCorretores === undefined) {
          console.warn('⚠️  [RESUMO] totalMsgResumoCorretores está UNDEFINED');
        }
        if (totRes.data.totalMsgResumoCorretores === null) {
          console.warn('⚠️  [RESUMO] totalMsgResumoCorretores está NULL');
        }
        if (totRes.data.totalMsgResumoCorretores === 0) {
          console.warn('⚠️  [RESUMO] totalMsgResumoCorretores é ZERO');
        }

        // Log detalhado da resposta de corretor
        console.log('📊 [CORRETOR] Resposta completa:', chartRes.data);
        console.log('📊 [CORRETOR] Status da resposta:', chartRes.status);
        console.log('📊 [CORRETOR] Tipo de dados:', Array.isArray(chartRes.data) ? 'Array' : typeof chartRes.data);
        console.log('📊 [CORRETOR] Quantidade de registros:', chartRes.data?.length || 0);

        // Atualiza resumo com dados recebidos
        // Usa || 0 para garantir valor numérico caso dados sejam undefined
        const resumoAtualizado = {
          gestores: totRes.data.totalMsgResumoGestores || 0,
          corretores: totRes.data.totalMsgResumoCorretores || 0,
          novoLead: totRes.data.totalMsgNovoLeadCorretores || 0
        };

        console.log('📋 [RESUMO] Dados que serão salvos no estado:', resumoAtualizado);

        setResumo(resumoAtualizado);
        console.log('✅ [RESUMO] Estado atualizado com sucesso');

        // Armazena dados brutos do gráfico
        const chartDataProcessed = chartRes.data || [];
        console.log('📊 [CHART] Dados do gráfico processados:', chartDataProcessed);

        setRawChartData(chartDataProcessed);
        console.log('✅ [CHART] Dados do gráfico salvos no estado');

      } catch (error) {

        if (
          error.response?.status === 401 ||
          error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
        ) {
          setForceTokenExpired(true); // Força abrir o diálogo
        }
        // Log detalhado de erro
        console.error('❌ [ERROR] Erro ao buscar dados do dashboard:', error);
        console.error('❌ [ERROR] Tipo do erro:', error.constructor.name);
        console.error('❌ [ERROR] Mensagem:', error.message);

        if (error.response) {
          console.error('❌ [ERROR] Status da resposta:', error.response.status);
          console.error('❌ [ERROR] Dados da resposta:', error.response.data);
          console.error('❌ [ERROR] Headers da resposta:', error.response.headers);

          // Logs específicos para diferentes tipos de erro
          if (error.response.status === 401) {
            console.error('🔒 [AUTH] Erro de autenticação - token pode estar expirado');
          } else if (error.response.status === 403) {
            console.error('🚫 [AUTH] Erro de autorização - sem permissão para acessar o recurso');
          } else if (error.response.status === 404) {
            console.error('🔍 [API] Endpoint não encontrado');
          } else if (error.response.status >= 500) {
            console.error('🔥 [SERVER] Erro interno do servidor');
          }
        } else if (error.request) {
          console.error('❌ [NETWORK] Erro de rede - requisição não recebeu resposta:', error.request);
        } else {
          console.error('❌ [UNKNOWN] Erro desconhecido:', error.message);
        }

        // Log de erro para debugging
        console.error('Erro ao buscar dados do dashboard:', error);
      } finally {
        // Finaliza estado de carregamento
        setLoading(false);
        console.log('✅ [LOADING] Estado de carregamento desativado');
        console.log('🏁 [DASHBOARD] Processo de busca finalizado');
      }
    };

    // Log antes de chamar a função
    console.log('🎯 [USEEFFECT] useEffect executado com dependências:', { dateFilter, baseURL });

    // Chama função de busca
    fetchDashboard();
  }, [dateFilter, baseURL, setForceTokenExpired]); // Dependências que trigam nova busca

  /**
   * Dados do gráfico ordenados
   * Utiliza useMemo para otimizar performance
   * Permite ordenação alfabética ou por total de mensagens
   */
  const sortedChartData = useMemo(() => {
    // Cria cópia do array para não modificar dados originais
    const data = [...rawChartData];

    // Ordenação alfabética (padrão)
    if (sortOption === 'alfabetica') {
      return data.sort((a, b) =>
        // Compara nomes considerando acentuação e case-insensitive
        a.nome.localeCompare(b.nome, 'pt', { sensitivity: 'base' })
      );
    }
    // Ordenação por total de mensagens
    else {
      return data.sort((a, b) => b.totalMensagens - a.totalMensagens);
    }
  }, [rawChartData, sortOption]); // Recalcula apenas quando dados ou opção mudam

  // Retorna dados e funções úteis para o componente
  return {
    loading,        // Estado de carregamento
    resumo,         // Resumo de métricas
    sortOption,     // Opção atual de ordenação
    setSortOption,  // Função para alterar ordenação
    chartData: sortedChartData  // Dados do gráfico ordenados
  };
}